import {collapseSpaces} from '@packages/systems/core/utils/TextUtils';
import {strLeft, strRight} from '@packages/utilities/string-utils';
import {humanizeRgb} from '@packages/systems/core/utils/ColorUtils';
import {
  VENDOR_PROPERTIES_SUPPORTED,
  CSS_PROPERTY_DETAILS,
  WebflowCssPropertyFull,
} from '@packages/systems/style/properties';
import {isVariableDefinitionPlaceholder} from '@packages/systems/core/utils/StyleVariables';

const rgbRegex = /rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)/g;
const rgbaRegex = /rgba\((\d{1,3},\s*\d{1,3},\s*\d{1,3},\s*0\.\d{3})(\d+)\)/g;
const vendorRegex = /^-(webkit|moz|ms|o)-/;

type CssPropertyName = string;
type CssPropertyValue = string | string[];

export type CssProperty = {
  property: CssPropertyName;
  value: CssPropertyValue;
};

/**
 * Removes the vendor prefix with exception of the following:
 * -webkit-text-fill-color
 * -webkit-text-stroke-width
 * -webkit-text-stroke-color
 * @param  {String} value A CSS value.
 * @return {String}       The same value with any vendor prefixes stripped.
 */
export function stripVendorPrefix(cssValue: string) {
  if (VENDOR_PROPERTIES_SUPPORTED.has(cssValue)) {
    return cssValue;
  }
  return stripAllVendorPrefixes(cssValue);
}

/**
 * Removes all vendor prefixes:
 * @param  {String} value A CSS value.
 * @return {String}       The same value with any vendor prefixes stripped.
 */
export function stripAllVendorPrefixes(cssValue: string) {
  return cssValue && cssValue[0] === '-'
    ? cssValue.replace(vendorRegex, '')
    : cssValue;
}

/**
 * Splits a CSS value, but ignores everything between parens.
 * @param  {String}   cssValue    The string to split, e.g. "0 0 1px rgba(255, 255, 255, 1), 1px 1px 0 #efefef"
 * @param  {String}   separator   The value separator, e.g. ","
 * @return {String[]}             An array of distinct values.
 */
export function splitCssValue(cssValue: string | null, separator: string) {
  const parts: Array<any | string> = [];
  let parens = 0;
  let comments = 0;
  let start = 0;

  if (cssValue === null || cssValue === undefined) {
    return [];
  }

  cssValue = String(collapseSpaces(cssValue));

  // Convert multi-char comments to single character for easier processing
  cssValue = cssValue.replace(/\/\*/g, '{').replace(/\*\//g, '}');

  // Convert comma + comment combos to special characters
  if (separator === ',') {
    cssValue = cssValue.replace(/\{,/g, '[').replace(/,\}/g, ']');
  }

  // Remove all spacing around comments
  cssValue = cssValue.replace(/\s*(\{|\}|\[|])\s*/g, '$1');

  for (let i = 0, j = cssValue.length; i < j; i++) {
    const ch = cssValue.charAt(i);
    const last = i + 1 === j;

    if (last) {
      const part = cssValue.substr(start).trim();
      if (part) {
        parts.push(cleanPart(part));
      }
    } else if (ch === '(') {
      parens++;
    } else if (ch === ')') {
      parens--;
    } else if (ch === '{' || ch === '[') {
      comments++;

      // Special case for comments that start with a comma, or opening comments when separated by a space - treat as a separator
      if (comments === 1 && (ch === '[' || (ch === '{' && separator === ' '))) {
        const part = cssValue.substr(start, i - start).trim();
        if (part) {
          parts.push(cleanPart(part));
        }
        start = i;
      }
    } else if (ch === '}' || ch === ']') {
      comments--;

      // If this is the end of a comment, assume it's the end of a part
      if (comments === 0) {
        const part = cssValue.substr(start, i - start + 1).trim();
        if (part) {
          parts.push(cleanPart(part));
        }
        start = i + 1;
      }
    } else if (ch === separator) {
      // Only separate if we are outside of a paren block or comment
      if (parens === 0 && comments === 0) {
        const part = cssValue.substr(start, i - start).trim();
        if (part) {
          parts.push(cleanPart(part));
        }
        start = i + 1;
      }
    } else {
      // Some non-separator, non-comment character
    }
  }

  return parts;
}

/**
 * 1st group: /(\b\{)/ — open brace, preceded by boundary
 * 2nd group: /(\{)/ — open brace
 * 3rd group: /(\})/ — closing brace
 * 4th group: /(\[)/ — open bracket
 * 5th group: /(])/ — closing bracket
 */
const commentRegex = /(\b\{)|(\{)|(\})|(\[)|(])/g;
function cleanPartReplacer(match: string, firstGroup: any) {
  switch (match) {
    case '{':
      return firstGroup ? ' /* ' : '/* ';
    case '}':
      return ' */';
    case '[':
      return '/*, ';
    case ']':
      return ', */';
  }
  return match;
}

function cleanPart(part: string) {
  // Convert back to proper comments
  return part.replace(commentRegex, cleanPartReplacer);
}

export function cssTextToPropertyValue(line: string): null | {
  property: string;
  value: string;
} {
  line = line.trim();
  if (line.length === 0) return null;

  const property = stripVendorPrefix(strLeft(line, ':').trim());

  // Ignore unsupported properties
  if (isUnsupportedCssProp(property)) return null;

  let value = strRight(line, ':').trim();
  if (value.indexOf('rgb') !== -1) {
    value = _humanizeRgb(_cleanRgba(value));
  }
  // Fix multi-axis background-repeat values to the traditional combined keywords
  if (property === 'background-repeat') {
    if (value === 'no-repeat no-repeat') {
      value = 'no-repeat';
    } else if (value === 'no-repeat repeat') {
      value = 'repeat-y';
    } else if (value === 'repeat no-repeat') {
      value = 'repeat-x';
    } else if (value === 'repeat repeat') {
      value = 'repeat';
    }
  }
  return {property, value};
}

/**
 * Converts a block of CSS text to property -> value pairs.
 * @param  {String}     cssText  The original CSS text, e.g. 'margin: 20px;'
 * @return {Object[]}            A list of value pairs, e.g. [ { property: 'margin', value: '20px' } ].
 */
export function cssToPropertyList(cssText: string): any {
  const list: Array<
    | any
    | {
        property: WebflowCssPropertyFull;
        value: string;
      }
  > = [];

  if (!cssText || cssText.trim().length === 0) {
    return list;
  }

  const lines = cssText.split(';');

  for (const line of lines) {
    const propertyValue = cssTextToPropertyValue(line);
    if (propertyValue == null) continue;

    list.push(propertyValue);
  }

  return list;
}

export function toStyleLess(props: {[key: string]: string}) {
  return Object.keys(props)
    .map((prop) => prop + ': ' + props[prop] + ';')
    .join(' ');
}

/**
 * Converts a block of CSS text to a { property: value } map.
 * @param  {String}  cssText   The original CSS text, e.g. 'margin: 20px;'
 * @param  {Boolean} onlyGridRelatedValues  If passed, returns `true` as value instead of the property value, with the exception of the display and grid-* values
 * @return {Object}            A map of CSS values, e.g. { margin: '20px' }.
 */
export function cssToPropertyMap(
  cssText: string | null,
  onlyGridRelatedValues?: boolean
): Record<string, any> {
  const propertyMap: Record<string, any> = {};

  if (!cssText || cssText.trim().length === 0) {
    return propertyMap;
  }

  const lines = cssText.split(';');

  for (const line of lines) {
    const entry = cssTextToPropertyValue(line);
    if (entry == null) continue;

    if (onlyGridRelatedValues) {
      switch (entry.property) {
        case 'display':
        case 'pointer-events':
        case 'width':
        case 'height':
        case 'justify-content':
        case 'align-content':
        case 'grid-area':
        case 'grid-auto-flow':
        case 'grid-auto-columns':
        case 'grid-auto-rows':
        case 'grid-column-gap':
        case 'grid-row-gap':
        case 'grid-column-start':
        case 'grid-row-start':
        case 'grid-column-end':
        case 'grid-row-end':
        case 'grid-template-areas':
        case 'grid-template-columns':
        case 'grid-template-rows':
        case 'border-top-width':
        case 'border-left-width':
        case 'border-bottom-width':
        case 'border-right-width':
        case 'background-color': {
          propertyMap[entry.property] = entry.value;
          continue;
        }
        default: {
          propertyMap[entry.property] = true;
        }
      }
    } else {
      propertyMap[entry.property] = entry.value;
    }
  }

  return propertyMap;
}

/**
 * Returns the CSS properties whose value differs between two declaration
 * strings — added, changed, or removed — paired with the resulting (modified)
 * value. Missing properties compare as an empty string, so a property present
 * in only one side is reported as changed.
 */
export function diffStyleLessProperties(
  original: string,
  modified: string
): Array<{property: string; value: string}> {
  const originalMap = cssToPropertyMap(original || '');
  const modifiedMap = cssToPropertyMap(modified || '');
  const properties = new Set([
    ...Object.keys(originalMap),
    ...Object.keys(modifiedMap),
  ]);

  const changed: Array<{property: string; value: string}> = [];
  for (const property of properties) {
    const before = String(originalMap[property] ?? '');
    const after = String(modifiedMap[property] ?? '');
    if (before !== after) {
      changed.push({property, value: after});
    }
  }
  return changed;
}

/**
 * Returns `true` if the provided CSS property name corresponds to a CSS
 * Variable.
 */
export function isCssVariableProperty(
  cssProp: string
): cssProp is `--${string}` {
  return cssProp.startsWith('--');
}

/**
 * Detects whether a CSS proprety is not supported.
 * @param  {String}  cssProp The CSS property name to check.
 * @return {Boolean}         True if the property name is on the denylist.
 */
export function isUnsupportedCssProp(cssProp: string): boolean {
  const isSupported =
    CSS_PROPERTY_DETAILS.hasOwnProperty(cssProp) ||
    isCssVariableProperty(cssProp) ||
    isVariableDefinitionPlaceholder(cssProp);
  return !isSupported;
}

/**
 * Converts an RGB color to a more readable hex or named color.
 * @param  {String} cssValue A CSS style value or list of values,
 *                           e.g. 'font-style: italic; color: rgb(255, 0, 0);'
 * @return {String}          The same value, but with RGB values cleaned up,
 *                           e.g. 'font-style: italic; color: red;'
 */
export function _humanizeRgb(cssValue: string) {
  return cssValue.replace(rgbRegex, humanizeRgb);
}

/**
 * Convert overly-precise RGBA opacity values from webkit
 * into more realistic values. Does not do any rounding.
 * @param  {String} cssValue A CSS style value or list of values, e.g.
 *                           'width: 1px; color: rgba(255, 0, 0, 0.78653)'
 * @return {String}          Same value, but with RGBA values cleaned up, e.g.
 *                           'width: 1px; color: rgba(255, 0, 0, 0.786)'
 */
export function _cleanRgba(cssValue: string) {
  return cssValue.replace(rgbaRegex, 'rgba($1)');
}
